/**
 * Add Sensing Station Form Schema
 * 
 * Schema for creating sensing stations with many-to-many site coverage relationship.
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// Re-export for convenience
export const ADD_SENSING_STATION_FORM_CONFIG = FORM_CONFIG
export const ADD_SENSING_STATION_FORM_NAME = FORM_NAME
export const ADD_SENSING_STATION_FORM_TITLE = FORM_TITLE
export const ADD_SENSING_STATION_FORM_DESCRIPTION = FORM_DESCRIPTION

// ============================================================================
// Form Schema (What user fills in = what gets sent to API)
// ============================================================================

export const AddSensingStationFormSchema = z.object({
  stationNumber: z.number().optional(), // Business key for edit mode
  corridorId: z.string().optional(), // UI-only: used to filter network sites
  name: z
    .string()
    .min(1, 'Name is required')
    .max(200, 'Name cannot exceed 200 characters'),
  geoLocation: z.object({
    latitude: z.preprocess(
      (val) => (val === '' || val == null ? undefined : Number(val)),
      z.number({ error: 'Latitude is required' })
        .min(-90, 'Latitude must be between -90 and 90')
        .max(90, 'Latitude must be between -90 and 90')
    ),
    longitude: z.preprocess(
      (val) => (val === '' || val == null ? undefined : Number(val)),
      z.number({ error: 'Longitude is required' })
        .min(-180, 'Longitude must be between -180 and 180')
        .max(180, 'Longitude must be between -180 and 180')
    ),
  }),
  addressIfAvailable: z
    .string()
    .optional(),
  stationRoadIntersections: z
    .string()
    .optional(),
  // Optional to match the backend (upsertStation has no coveredBySites field; it's
  // not required and isn't even part of the station upsert contract). UI-only.
  // Coerce items to string: on edit this is populated from the API, which may return
  // site NUMBERS — a plain z.string() would reject those and silently block the save.
  coveredBySites: z
    .array(z.coerce.string())
    .optional()
    .default([]),
  stationNotes: z
    .string()
    .optional(),
  stationPictures: z
    .array(z.any())
    .max(10, 'Maximum 10 pictures allowed')
    .optional()
    .default([]),
}).superRefine((data, ctx) => {
  // File upload validation (client-side only, when File objects are present)
  if (data.stationPictures && Array.isArray(data.stationPictures) && data.stationPictures.length > 0) {
    const files = data.stationPictures.filter((item): item is File => item instanceof File)
    const totalSize = files.reduce((sum, file) => sum + file.size, 0)
    const maxFileSize = 5 * 1024 * 1024 // 5MB
    const maxTotalSize = 25 * 1024 * 1024 // 25MB

    // Validate individual file sizes
    files.forEach((file, index) => {
      if (file.size > maxFileSize) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['stationPictures', index],
          message: `File "${file.name}" exceeds 5MB limit`,
        })
      }
      
      // Validate file types
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (!validTypes.includes(file.type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['stationPictures', index],
          message: `File "${file.name}" must be jpg, png, or webp`,
        })
      }
    })

    // Validate total size
    if (totalSize > maxTotalSize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stationPictures'],
        message: `Total file size exceeds 25MB limit (current: ${(totalSize / 1024 / 1024).toFixed(2)}MB)`,
      })
    }
  }
})

export type AddSensingStationFormData = z.infer<typeof AddSensingStationFormSchema>

/**
 * Transform form data to API request format.
 * Maps frontend field names to backend API field names.
 */
export function transformToApiRequest(formData: AddSensingStationFormData) {
  return {
    stationNumber: formData.stationNumber, // Business key for upsert
    stationName: formData.name.trim(),
    location: {
      latitude: formData.geoLocation.latitude,
      longitude: formData.geoLocation.longitude,
    },
    stationAddress: formData.addressIfAvailable?.trim() || '',
    stationRoadIntersections: formData.stationRoadIntersections?.trim() || undefined,
    stationNotes: formData.stationNotes?.trim() || undefined,
    coveredBySites: formData.coveredBySites || [],
    stationPictures: formData.stationPictures || [], // Files will be handled by API layer
  }
}
